import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, Plus, X, Save } from "lucide-react";
import contentInterviewService, { ContentInterview, ContentInterviewTopic } from "../../services/contentInterviewService";

const InterviewList: React.FC = () => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState<ContentInterview[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editingTopics, setEditingTopics] = useState<Record<number, ContentInterviewTopic[]>>({});
  const [saving, setSaving] = useState<number | null>(null);
  const [topicInput, setTopicInput] = useState("");

  useEffect(() => {
    loadInterviews();
  }, []);

  const loadInterviews = async () => {
    try {
      const data = await contentInterviewService.list();
      setInterviews(data);
    } catch (err) {
      console.error("Failed to load interviews:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    const interview = interviews.find((i) => i.id === id);
    if (interview && !editingTopics[id]) {
      setEditingTopics({ ...editingTopics, [id]: [...(interview.topics || [])] });
    }
  };

  const handleAddTopic = (interviewId: number) => {
    const title = topicInput.trim();
    if (!title) return;
    const current = editingTopics[interviewId] || [];
    setEditingTopics({
      ...editingTopics,
      [interviewId]: [...current, { title, source: "manual", notes: "", category: "" }],
    });
    setTopicInput("");
  };

  const handleRemoveTopic = (interviewId: number, index: number) => {
    const current = editingTopics[interviewId] || [];
    setEditingTopics({
      ...editingTopics,
      [interviewId]: current.filter((_, i) => i !== index),
    });
  };

  const handleSave = async (interviewId: number) => {
    setSaving(interviewId);
    try {
      const updated = await contentInterviewService.update(interviewId, {
        topics: editingTopics[interviewId],
      });
      setInterviews(interviews.map((i) => (i.id === interviewId ? updated : i)));
    } catch (err) {
      console.error("Failed to save topics:", err);
    } finally {
      setSaving(null);
    }
  };

  const hasChanges = (interviewId: number) => {
    const interview = interviews.find((i) => i.id === interviewId);
    if (!interview) return false;
    return JSON.stringify(editingTopics[interviewId]) !== JSON.stringify(interview.topics);
  };

  if (loading) {
    return (
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "60px 20px", textAlign: "center" }}>
        <p style={{ color: "#6B7280" }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "640px", margin: "0 auto", padding: "40px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
        <button
          onClick={() => navigate("/content-interviews")}
          style={{
            padding: "8px", borderRadius: "8px", border: "1px solid #D1D5DB",
            backgroundColor: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center",
          }}
        >
          <ArrowLeft size={16} />
        </button>
        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#111827", margin: 0 }}>
          Your Interviews
        </h1>
      </div>

      {interviews.length === 0 ? (
        <p style={{ color: "#6B7280", textAlign: "center", padding: "40px 0" }}>
          No paid interviews yet.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {interviews.map((interview) => {
            const isExpanded = expandedId === interview.id;
            const topics = editingTopics[interview.id] || interview.topics || [];
            const changed = hasChanges(interview.id);
            const isSaving = saving === interview.id;

            return (
              <div key={interview.id} style={{
                borderRadius: "10px", border: "1px solid #E5E7EB", backgroundColor: "#FAFAFA",
                overflow: "hidden",
              }}>
                <button
                  onClick={() => handleExpand(interview.id)}
                  style={{
                    width: "100%", padding: "16px 20px", border: "none", backgroundColor: "transparent",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: "10px", textAlign: "left",
                  }}
                >
                  {isExpanded
                    ? <ChevronDown size={16} color="#6B7280" />
                    : <ChevronRight size={16} color="#6B7280" />
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#111827" }}>
                      {interview.pricing_plan === "pack" ? "4-Session Pack" : "Single Session"}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>
                      {new Date(interview.created_at).toLocaleDateString()} &middot; {(interview.topics || []).length} topics
                    </div>
                  </div>
                  <span style={{
                    fontSize: "12px", fontWeight: 500, color: "#059669", backgroundColor: "#D1FAE5",
                    padding: "2px 8px", borderRadius: "12px",
                  }}>
                    Paid
                  </span>
                </button>

                {isExpanded && (
                  <div style={{ padding: "0 20px 20px" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
                      {topics.map((t, i) => (
                        <div key={i} style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          padding: "6px 12px", backgroundColor: "#F3F4F6",
                          borderRadius: "20px", fontSize: "13px", color: "#374151",
                          border: "1px solid #E5E7EB",
                        }}>
                          <span>{t.title}</span>
                          {t.category && (
                            <span style={{
                              fontSize: "11px", color: "#9CA3AF", backgroundColor: "#E5E7EB",
                              padding: "1px 6px", borderRadius: "8px",
                            }}>
                              {t.category}
                            </span>
                          )}
                          <button
                            onClick={() => handleRemoveTopic(interview.id, i)}
                            style={{
                              display: "inline-flex", alignItems: "center", background: "none",
                              border: "none", cursor: "pointer", padding: "2px", color: "#9CA3AF",
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                      <input
                        type="text"
                        value={expandedId === interview.id ? topicInput : ""}
                        onChange={(e) => setTopicInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddTopic(interview.id)}
                        placeholder="Add a topic..."
                        style={{
                          flex: 1, padding: "8px 12px", borderRadius: "8px",
                          border: "1px solid #D1D5DB", fontSize: "13px", outline: "none",
                        }}
                      />
                      <button
                        onClick={() => handleAddTopic(interview.id)}
                        disabled={!topicInput.trim()}
                        style={{
                          padding: "8px 12px", borderRadius: "8px", border: "none",
                          backgroundColor: "#6366F1", color: "#FFFFFF", fontSize: "13px",
                          fontWeight: 600, cursor: !topicInput.trim() ? "not-allowed" : "pointer",
                          opacity: !topicInput.trim() ? 0.5 : 1,
                          display: "flex", alignItems: "center", gap: "4px",
                        }}
                      >
                        <Plus size={14} /> Add
                      </button>
                    </div>

                    {changed && (
                      <button
                        onClick={() => handleSave(interview.id)}
                        disabled={isSaving}
                        style={{
                          padding: "8px 16px", borderRadius: "8px", border: "none",
                          backgroundColor: "#059669", color: "#FFFFFF", fontSize: "13px",
                          fontWeight: 600, cursor: isSaving ? "not-allowed" : "pointer",
                          opacity: isSaving ? 0.7 : 1,
                          display: "inline-flex", alignItems: "center", gap: "6px",
                        }}
                      >
                        <Save size={14} /> {isSaving ? "Saving..." : "Save Changes"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InterviewList;
